<template>
    <div v-show="show">
        <QueryInput v-model="query" ref="queryInput" size="is-small" />

        <Checkbox2 v-model="hideEmpty" @change="onHideEmptyChanged">
            {{ translate("admin_categories.hide_empty_categories") }}
        </Checkbox2>

        <div class="mb-3 mt-2" v-if="initDone" ref="listsContainer">
            <!-- The optional aside is a second column beside the list; both lists have the default SelectionList height, so they line up. -->
            <div :class="{ columns: $slots.aside }">
                <div :class="{ column: $slots.aside }">
                    <p v-if="categories.length === 0 && !loading" class="has-text-grey-light">
                        {{ translate("bracket_category_select.no_categories_found") }}
                    </p>
                    <SelectionList
                        v-else
                        :options="categories"
                        :is-loading="loading"
                        v-model="selectedCategory"
                        :comparing-function="isSameCategory"
                        :filter-query="query"
                        :filter-function="category => category.value"
                        @change="onChange"
                    >
                        <template #option="{ option }">
                            <div class="is-flex is-justify-content-space-between is-align-items-center">
                                <span>{{ option.value }}</span>
                                <span v-if="option.key === currentParameterId" class="tag is-info is-light ml-2">
                                    {{ translate("admin_competitors.current_category") }}
                                </span>
                            </div>
                        </template>
                    </SelectionList>
                </div>
                <div v-if="$slots.aside" class="column">
                    <slot name="aside"></slot>
                </div>
            </div>
        </div>
        <div class="mb-3 has-text-centered" v-else>
            <LoadingSpinner2 />
        </div>

        <!-- The warning shares the row of the selected category, so showing it does not push the content below. -->
        <p v-if="showSelectedCategory">
            <span class="has-text-grey-light">{{ translate("admin_competitors.selected_category") }}</span>
            <span v-if="selectedCategory" :class="selectedCategoryWarning ? 'has-text-warning' : 'has-text-success'">
                {{ selectedCategory.value }}
            </span>
            <span v-else>{{ translate("common.none") }}</span>
            <span v-if="selectedCategory && selectedCategoryWarning" class="has-text-warning ml-2" role="alert">
                - {{ selectedCategoryWarning }}
            </span>
        </p>
    </div>
</template>

<script setup lang="ts">
    import { nextTick, ref } from "vue";
    import Checkbox2 from "../Common/Checkbox2.vue";
    import LoadingSpinner2 from "../Common/LoadingSpinner2.vue";
    import QueryInput from "../Common/QueryInput.vue";
    import SelectionList from "../Common/SelectionList.vue";
    import { translate } from "../Common/translations";
    import { handleError } from "../Common/errorHandler";
    import categoriesApi from "../Api/Categories/categories";
    import { Category } from "../Common/types";

    const props = defineProps<{
        eventId: number;
        showSelectedCategory?: boolean;
        selectedCategoryWarning?: string;
        currentParameterId?: number;
    }>();

    const emit = defineEmits<{
        selected: [category: Category];
        selectionCleared: [];
    }>();

    const queryInput = ref<InstanceType<typeof QueryInput> | null>(null);
    const listsContainer = ref<HTMLElement | null>(null);
    const show = ref(false);
    const initDone = ref(false);
    const query = ref("");
    const selectedCategory = ref<Category | null>(null);
    const categories = ref<Category[]>([]);
    const loading = ref(false);
    const hideEmpty = ref(true);
    let latestCategoriesRequest = 0;

    function isSameCategory(a: Category, b: Category) {
        return a.key === b.key;
    }

    function init() {
        selectedCategory.value = null;
        queryInput.value?.clearSearch();
        query.value = "";
        hideEmpty.value = true;
        initDone.value = false;
        show.value = true;

        return loadCategories()
            .then(() => nextTick())
            .then(() => {
                scrollToCurrentCategory();
            })
            .catch(handleError);
    }

    function onHideEmptyChanged() {
        selectedCategory.value = null;
        emit("selectionCleared");

        loadCategories().catch(handleError);
    }

    //Brings the current category into view (without selecting it), so that its neighbours are visible.
    function scrollToCurrentCategory() {
        const index = categories.value.findIndex(category => category.key === props.currentParameterId);

        listsContainer.value?.querySelectorAll(".option-wrapper")[index]?.scrollIntoView({ behavior: "instant", block: "center" });
    }

    function close() {
        show.value = false;
    }

    function loadCategories(): Promise<void> {
        const request = ++latestCategoriesRequest;
        loading.value = true;

        //The list stays rendered while reloading (SelectionList shows a spinner overlay), so the modal height doesn't jump.
        return categoriesApi
            .getCategories(props.eventId, { hideEmpty: hideEmpty.value })
            .then(result => {
                //Only the latest request may render, so quick toggling can't show a stale list.
                if (request !== latestCategoriesRequest) {
                    return;
                }

                categories.value = result;
                initDone.value = true;
            })
            .finally(() => {
                if (request === latestCategoriesRequest) {
                    loading.value = false;
                }
            });
    }

    function onChange(category: Category | null) {
        if (category) {
            emit("selected", category);
        }
    }

    defineExpose({ init, close });
</script>
